import styled from "@emotion/styled";

import {
  MDSButton,
  MDSButtonSize,
  MDSButtonSlotName,
  MDSButtonVariant,
  MDSIcon,
  MDSIconKind,
} from "@/mds";
import { useHasAgentIntentModel } from "@/pages/agent/components/agent-intent-model-context";
import { useAppStore } from "@/store";

export enum AgentSurfaceNavigationActiveTab {
  Goals = "goals",
  Projects = "projects",
  Tasks = "tasks",
  Information = "information",
  Routines = "routines",
  Memory = "memory",
  Skills = "skills",
  Activities = "activities",
}

export const AgentSurfaceNavigation = ({
  activeTab,
}: {
  activeTab: AgentSurfaceNavigationActiveTab;
}) => {
  const { store } = useAppStore();
  const supportsGoals = useHasAgentIntentModel();
  const fullAgentIsEnabled = store.agentOverlay.isFeatureEnabled;

  return (
    <StyledSurfaceTabs aria-label="Agent pages">
      <StyledPrimaryTabs>
        {supportsGoals && fullAgentIsEnabled ? (
          <StyledSurfaceTab
            $isActive={activeTab === AgentSurfaceNavigationActiveTab.Goals}
            size={MDSButtonSize.Small}
            variant={
              activeTab === AgentSurfaceNavigationActiveTab.Goals
                ? MDSButtonVariant.Chrome
                : MDSButtonVariant.Transparent
            }
            onPress={() => {
              store.agentOverlay.openGoals();
            }}
          >
            <MDSIcon slot={MDSButtonSlotName.LeftIcon} kind={MDSIconKind.Compass} />
            Goals
          </StyledSurfaceTab>
        ) : null}
        <StyledSurfaceTab
          $isActive={activeTab === AgentSurfaceNavigationActiveTab.Projects}
          size={MDSButtonSize.Small}
          variant={
            activeTab === AgentSurfaceNavigationActiveTab.Projects
              ? MDSButtonVariant.Chrome
              : MDSButtonVariant.Transparent
          }
          onPress={() => {
            store.agentOverlay.openProjects();
          }}
        >
          <MDSIcon slot={MDSButtonSlotName.LeftIcon} kind={MDSIconKind.Folder} />
          Projects
        </StyledSurfaceTab>
        <StyledSurfaceTab
          $isActive={activeTab === AgentSurfaceNavigationActiveTab.Tasks}
          size={MDSButtonSize.Small}
          variant={
            activeTab === AgentSurfaceNavigationActiveTab.Tasks
              ? MDSButtonVariant.Chrome
              : MDSButtonVariant.Transparent
          }
          onPress={() => {
            store.agentOverlay.openTasks();
          }}
        >
          <MDSIcon slot={MDSButtonSlotName.LeftIcon} kind={MDSIconKind.ListCheck} />
          Tasks
        </StyledSurfaceTab>
        {fullAgentIsEnabled ? (
          <StyledSurfaceTab
            $isActive={activeTab === AgentSurfaceNavigationActiveTab.Routines}
            size={MDSButtonSize.Small}
            variant={
              activeTab === AgentSurfaceNavigationActiveTab.Routines
                ? MDSButtonVariant.Chrome
                : MDSButtonVariant.Transparent
            }
            onPress={() => {
              store.agentOverlay.openRoutines();
            }}
          >
            <MDSIcon slot={MDSButtonSlotName.LeftIcon} kind={MDSIconKind.Clock} />
            Routines
          </StyledSurfaceTab>
        ) : null}
        {store.agentOverlay.isMemoryFeatureEnabled && (
          <StyledSurfaceTab
            $isActive={activeTab === AgentSurfaceNavigationActiveTab.Memory}
            size={MDSButtonSize.Small}
            variant={
              activeTab === AgentSurfaceNavigationActiveTab.Memory
                ? MDSButtonVariant.Chrome
                : MDSButtonVariant.Transparent
            }
            onPress={() => {
              store.agentOverlay.openMemory();
            }}
          >
            <MDSIcon slot={MDSButtonSlotName.LeftIcon} kind={MDSIconKind.Cells} />
            Memory
          </StyledSurfaceTab>
        )}
        {store.agentOverlay.isSkillsFeatureEnabled && (
          <StyledSurfaceTab
            $isActive={activeTab === AgentSurfaceNavigationActiveTab.Skills}
            size={MDSButtonSize.Small}
            variant={
              activeTab === AgentSurfaceNavigationActiveTab.Skills
                ? MDSButtonVariant.Chrome
                : MDSButtonVariant.Transparent
            }
            onPress={() => {
              store.agentOverlay.openSkills();
            }}
          >
            <MDSIcon slot={MDSButtonSlotName.LeftIcon} kind={MDSIconKind.PuzzlePiece} />
            Skills
          </StyledSurfaceTab>
        )}
      </StyledPrimaryTabs>
      {fullAgentIsEnabled ? (
        <StyledSurfaceTab
          $isActive={activeTab === AgentSurfaceNavigationActiveTab.Activities}
          size={MDSButtonSize.Small}
          variant={
            activeTab === AgentSurfaceNavigationActiveTab.Activities
              ? MDSButtonVariant.Chrome
              : MDSButtonVariant.Transparent
          }
          onPress={() => {
            store.agentOverlay.openActivities();
          }}
        >
          <MDSIcon slot={MDSButtonSlotName.LeftIcon} kind={MDSIconKind.History} />
          Activity Log
        </StyledSurfaceTab>
      ) : null}
    </StyledSurfaceTabs>
  );
};

const StyledSurfaceTabs = styled.nav`
  align-items: center;
  display: flex;
  flex-wrap: wrap;
  gap: ${({ theme }) => theme.spacing[2]};
  justify-content: space-between;
`;

const StyledPrimaryTabs = styled.div`
  align-items: center;
  display: flex;
  flex-wrap: wrap;
  gap: ${({ theme }) => theme.spacing[2]};
`;

const StyledSurfaceTab = styled(MDSButton)<{ $isActive: boolean }>`
  flex: 0 0 auto;

  ${({ $isActive, theme }) =>
    $isActive
      ? `
        &[data-hovered]:not([data-disabled]) {
          background: ${theme.colors.background.button.chrome.default};
        }
      `
      : ""}
`;
